import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Progress } from "antd";
import {
  CheckCircleFilled,
  CloseOutlined,
  RightOutlined,
  ReloadOutlined,
  RocketOutlined,
} from "@ant-design/icons";
import { useSetupProgressContext } from "./SetupProgressContext";
import ProductSetupHint from "./ProductSetupHint";
import "./setupGuide.css";

/**
 * First-run "Setup Guide" - a friendly, AI-voiced onboarding companion that
 * hand-holds a new merchant through getting their store ready to sell. Floating
 * launcher (bottom-left); auto-opens once for first-time merchants. Progress is
 * shared with the Dashboard SetupCard via useSetupProgress.
 */
export default function SetupGuide() {
  const history = useHistory();
  const sp = useSetupProgressContext();
  const [open, setOpen] = useState(false);

  // Auto-open once for a first-time merchant (no products yet), or always in
  // preview/test mode so the first-run experience is easy to see.
  useEffect(() => {
    if (!sp || !sp.loggedIn || sp.detected.loading) return;
    if (sp.allDone || sp.state.completed || sp.state.dismissed) return;
    if (sp.preview) {
      setOpen(true);
      return;
    }
    if (sp.state.seen) return;
    if (!sp.detected.products) setOpen(true);
    sp.persist({ ...sp.state, seen: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp && sp.loggedIn, sp && sp.detected.loading, sp && sp.preview, sp && sp.allDone]);

  if (!sp || !sp.loggedIn || sp.allDone) return null;

  const { doneCount, total, pct, activeStep, steps, sellsVariants } = sp;

  const goTo = (step) => {
    if (step.soft) sp.markDone(step);
    setOpen(false);
    history.push(step.route);
  };

  const restart = () => {
    sp.restart();
    setOpen(true);
  };

  const greeting =
    doneCount === 0
      ? "👋 Welcome to Kuber! I'm your setup buddy. Let's get your store ready to sell - I'll walk you through it, one step at a time."
      : `Nice progress - ${doneCount} of ${total} done. Let's keep going; here's what's next.`;

  return (
    <>
      {!open && (
        <button
          type="button"
          className="setup-guide-launcher"
          onClick={() => setOpen(true)}
          aria-label="Open setup guide"
        >
          <Progress
            type="circle"
            percent={pct}
            width={34}
            strokeColor="#eb4a1c"
            strokeWidth={10}
            format={() => `${doneCount}/${total}`}
            className="setup-guide-launcher-ring"
          />
          <span className="setup-guide-launcher-label">Finish setup</span>
        </button>
      )}

      {open && (
        <div className="setup-guide-panel" role="dialog" aria-label="Store setup guide">
          <div className="setup-guide-header">
            <div className="setup-guide-header-title">
              <RocketOutlined className="setup-guide-header-icon" />
              <span>Store setup</span>
            </div>
            <button
              type="button"
              className="setup-guide-icon-btn"
              onClick={() => setOpen(false)}
              aria-label="Hide guide"
            >
              <CloseOutlined />
            </button>
          </div>

          <div className="setup-guide-greeting">{greeting}</div>

          <div className="setup-guide-progress">
            <Progress percent={pct} strokeColor="#eb4a1c" showInfo={false} />
            <span className="setup-guide-progress-label">
              {doneCount} of {total} done
            </span>
          </div>

          <label className="setup-guide-variant-toggle">
            <input type="checkbox" checked={sellsVariants} onChange={sp.toggleVariants} />
            <span>I'll sell products with sizes or colours (variants)</span>
          </label>

          <ProductSetupHint hint={sp.productSetupHint} />

          <div className="setup-guide-steps">
            {steps.map((step) => {
              const done = sp.isDone(step);
              const userSkipped = sp.isSkipped(step);
              const isCurrent = activeStep && activeStep.id === step.id;
              const optionalMuted = step.optional && !sellsVariants && !done && !userSkipped;
              const showActions = !done && !userSkipped;
              return (
                <div
                  key={step.id}
                  className={`setup-guide-step${done ? " is-done" : ""}${
                    userSkipped || optionalMuted ? " is-skipped" : ""
                  }${isCurrent ? " is-current" : ""}`}
                >
                  <div className="setup-guide-step-status" aria-hidden>
                    {done ? <CheckCircleFilled /> : <span className="setup-guide-step-dot" />}
                  </div>
                  <div className="setup-guide-step-body">
                    <div className="setup-guide-step-title">
                      {step.title}
                      {step.optional && (
                        <span className="setup-guide-step-optional">
                          {sellsVariants ? "for variants" : "optional"}
                        </span>
                      )}
                      {userSkipped && <span className="setup-guide-step-optional">skipped</span>}
                    </div>
                    {showActions && <div className="setup-guide-step-copy">{step.copy}</div>}
                    {showActions && (
                      <div className="setup-guide-step-actions">
                        <button
                          type="button"
                          className={`setup-guide-step-btn${isCurrent ? " is-primary" : ""}`}
                          onClick={() => goTo(step)}
                        >
                          Take me there <RightOutlined />
                        </button>
                        <button type="button" className="setup-guide-link sm" onClick={() => sp.markDone(step)}>
                          Mark done
                        </button>
                        {!step.noSkip && (
                          <button type="button" className="setup-guide-link sm" onClick={() => sp.skipStep(step)}>
                            Skip
                          </button>
                        )}
                      </div>
                    )}
                    {userSkipped && !step.noSkip && (
                      <button type="button" className="setup-guide-link sm" onClick={() => sp.unskip(step)}>
                        Undo skip
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="setup-guide-footer">
            <button type="button" className="setup-guide-link" onClick={restart}>
              <ReloadOutlined /> Restart walkthrough
            </button>
            <button
              type="button"
              className="setup-guide-link"
              onClick={() => {
                sp.dismiss();
                setOpen(false);
              }}
            >
              I&apos;ve finished setup - hide this
            </button>
          </div>
        </div>
      )}
    </>
  );
}
